<template>
	<div ref="rowSliderRef">
		<div
			v-if="!dataLoaded || (categoryData.length > 0 && dataLoaded)"
			class="category-title"
			@mouseenter="showButton = true"
			@mouseleave="showButton = false"
		>
			<h2 class="vl-font-weight--medium">{{ categoryName }}</h2>
			<Transition>
				<div v-if="showButton" class="input-wrapper">
					<button class="search-with-id" @click="navigateToCategoryById">
						{{ $t("teaser.see_more") }}
						<IconViewMore />
					</button>
				</div>
			</Transition>
		</div>
		<div
			v-if="categoryData.length > 0 && dataLoaded"
			v-focus-section
			class="row-slider-section"
			:class="[`category-${category}`, teaserStore.state.isPreviewing ? 'is-previewing-teaser' : '']"
			@blur="handleBlur"
		>
			<!-- A single Splide component with dynamically bound options -->
			<div class="splide__arrows" v-if="renderArrows">
				<button v-focus @click="prevSlide(splideRef)" class="splide__arrow splide__arrow--prev">
					<IconArrow />
				</button>
				<button v-focus @click="nextSlide(splideRef)" class="splide__arrow splide__arrow--next">
					<IconArrow />
				</button>
			</div>
			<Splide ref="splideRef" :options="splideOptions" aria-label="My Favorite movies" v-if="categoryData.length > 0">
				<SplideSlide
					:data-id="item?.drupal_internal__nid"
					v-for="(item, key) in categoryData"
					@click="handleActiveSplideClick(item)"
					:key="key"
					:class="{
						top10: preview === 'option3',
						first_item: key === firstVisibleIndex,
						last_item: key === lastVisibleIndex,
						'first-visible': key === firstVisibleIndex,
						'last-visible': key === lastVisibleIndex,
					}"
				>
					<!-- Conditionally render the number for the 'numbered' preview -->
					<h1
						v-if="preview !== 'option1' && preview !== 'option2'"
						class="vl-font-size--390 color-gray font-family--sans-outline-exb numbered-title"
					>
						{{ key + 1 }}
					</h1>
					<!-- Dynamically render the correct teaser component -->
					<component
						:isSubComponent="false"
						:isPreviewing="isPreviewing && previewingItem === key"
						:is="getTeaserComponent(item)"
						:item="item"
						:isContinueWatching="category === 44"
						:key="item?.drupal_internal__nid"
						:isFirstVisibleElement="key === firstVisibleIndex"
						:isLastVisibleElement="key === lastVisibleIndex"
						@preview-ended="isPreviewing = false"
					/>
				</SplideSlide>
			</Splide>
		</div>
		<pulse-loader v-if="!dataLoaded" :loading="true" :color="'white'" :size="'12px'"></pulse-loader>
	</div>
</template>

<script lang="ts" setup>
	import { computed, ref, watch } from "vue"
	import { useMovieStore } from "../stores/movies"
	import { useTaxonomyStore } from "../stores/taxonomies"
	import { TeaserSmall, TeaserLarge, TeaserWithNumber } from "./teasers"
	import { useSeriesStore } from "../stores/series"
	import { useRoute, useRouter } from "vue-router"
	import { Movie } from "../types/Movie"
	import { Series } from "../types/Series"
	import { isNativeDevice } from "../helper"
	import IconArrow from "../icons/IconArrow.vue"
	import { Splide as SplideType } from "@splidejs/splide"
	import IconViewMore from "../icons/IconViewMore.vue"
	import { useTeaserStore } from "../stores/teaser"
	import { useSplide } from "../composables/useSplide"
	import { useWindowSize } from "@vueuse/core"
	const { nextSlide, prevSlide } = useSplide()
	const teaserStore = useTeaserStore()
	const seriesStore = useSeriesStore()
	const movieStore = useMovieStore()
	const taxonomyStore = useTaxonomyStore()
	const route = useRoute()
	const router = useRouter()
	const showButton = ref(false)
	const isPreviewing = ref(false)
	const previewingItem = ref<number | null>(null)
	const splideRef = ref<any>(null)
	const hoverTimeout = ref<number | null>(null)
	const rowSliderRef = ref<HTMLElement | null>(null)
	const foundMovies = ref<Movie[]>([])
	const foundSeries = ref<Series[]>([])
	const { width } = useWindowSize()

	const props = defineProps<{
		category: number
		preview: string
		dataLoaded?: boolean
	}>()

	// these two variables are used to track first and last visible items
	// and alter the preview teasers accordingly.
	const firstVisibleIndex = ref(0)
	const lastVisibleIndex = ref(5)

	// @todo: this should become a computed property
	const categoryName = computed(() => taxonomyStore.getCategoryTitle(props.category))

	/**
	 * A helper function to deduplicate items by their internal ID.
	 * This is particularly useful when combining movies and series
	 * to avoid showing the same item twice.
	 * @param arr
	 * @returns deduplicated array
	 */
	const removeDuplicates = (arr) => {
		const seen = new Set()
		return (arr || []).filter((item) => {
			const id = item?.drupal_internal__nid
			if (!id || seen.has(id)) return false
			seen.add(id)
			return true
		})
	}
	/**
	 * The basic computed property to get category data based on the category prop.
	 * It checks the current route to filter movies, series, or both and returns
	 * the appropriate data set (either movies, series, or a combined array).
	 */
	const categoryData = computed(() => {
		const taxonomyOrdering = taxonomyStore.state.categories.find(
			(cat) => cat.drupal_internal__tid === props.category
		)?.field_priority_nodes

		if (props.category === 45) {
			foundMovies.value = movieStore.state.profileMoviesList.find((item: any) => item.category === props.category)
			foundSeries.value = seriesStore.state.profileSeriesList.find((item: any) => item.category === props.category)
		} else if (props.category === 44) {
			foundMovies.value = movieStore.state.watchingMoviesList.find((item: any) => item.category === props.category)
			foundSeries.value = seriesStore.state.watchingSeriesList.find((item: any) => item.category === props.category)
		} else {
			foundMovies.value = movieStore.state.movies.find((item: any) => item.category === props.category)
			foundSeries.value = seriesStore.state.series.find((item: any) => item.category === props.category)
		}
		// console.log("foundMovies", foundMovies.value)
		// console.log("foundSeries", foundSeries.value)

		// Filter based on route type
		if (route.name === "MoviesPage") {
			const data = (foundMovies.value as any)?.data || []
			return Array.isArray(data) ? data : []
		} else if (route.name === "SeriesPage") {
			const data = (foundSeries.value as any)?.data || []
			return Array.isArray(data) ? data : []
		}

		// Home route - return both
		const moviesData = (foundMovies.value as any)?.data
		const seriesData = (foundSeries.value as any)?.data

		if (!foundMovies && !foundSeries) {
			return []
		}
		const combined = [moviesData, seriesData].filter(Boolean).flat()
		const deduplicatedCombined = removeDuplicates(combined)

		// const combinedResults = [...moviesData, ...seriesData]
		// Performs a quick sort based on taxonomy ordering if available through the json:api response.
		// All values present in taxonomyOrdering are sorted first, followed by the rest sorted by creation date.
		if (taxonomyOrdering && taxonomyOrdering.length > 0 && props.category !== 44 && props.category !== 45) {
			deduplicatedCombined.sort((a, b) => {
				const aIndex = taxonomyOrdering.findIndex((item) => item.id === a.id)
				const bIndex = taxonomyOrdering.findIndex((item) => item.id === b.id)

				// Both items are in taxonomyOrdering - sort by their position
				if (aIndex !== -1 && bIndex !== -1) {
					return aIndex - bIndex
				}
				// Only 'a' is in taxonomyOrdering - it comes first
				if (aIndex !== -1) {
					return -1
				}
				// Only 'b' is in taxonomyOrdering - it comes first
				if (bIndex !== -1) {
					return 1
				}
				// Neither is in taxonomyOrdering - sort by created field
				const aCreated = a.attributes?.created || a.created || ""
				const bCreated = b.attributes?.created || b.created || ""
				return bCreated.localeCompare(aCreated) // Descending order (newest first)
			})
		} else if (props.category === 44 || props.category === 45) {
			// For Continue Watching, sort by last watched timestamp if available
			deduplicatedCombined.sort((a, b) => {
				const aTimestamp = new Date(a.flag_changed) || 0
				const bTimestamp = new Date(b.flag_changed) || 0
				// return bTimestamp - aTimestamp // Descending order (most recent first)
				return bTimestamp.getTime() - aTimestamp.getTime() // Descending order (most recent first)
			})
		}

		return Array.isArray(deduplicatedCombined) ? deduplicatedCombined : []
	})

	/**
	 * A basic handler for active splide item clicks, that navigates to the detailed view.
	 * @used in Native Devices
	 * @param item
	 */
	const handleActiveSplideClick = (item: any) => {
		if (isNativeDevice) {
			router.push({
				name: item.type === "node--movie" ? "InnerMovie" : "InnerSeries",
				params: { id: item.drupal_internal__nid },
			})
		}
	}

	/**
	 * @deprecated
	 * Safely deprecate this method after confirming no side effects in
	 * native devices.
	 */
	// const handleFocus = (item: any, key: any) => {
	// 	// return
	// 	// Clear any existing timeout
	// 	if (hoverTimeout.value) {
	// 		// @ts-ignore
	// 		clearTimeout(hoverTimeout.value)
	// 	}
	// 	// Set a 3-second delay before showing the preview
	// 	hoverTimeout.value = window.setTimeout(() => {
	// 		isPreviewing.value = true
	// 		previewingItem.value = key
	// 	}, 500)
	// }

	/**
	 * @deprecated
	 * Safely deprecate this method after confirming no side effects in
	 * native devices.
	 */
	const handleBlur = (event: FocusEvent) => {
		// Clear the timeout if user leaves before 3 seconds
		if (hoverTimeout.value) {
			clearTimeout(hoverTimeout.value)
			hoverTimeout.value = null
		}
	}

	/**
	 * Navigate to the category page based on the current route context.
	 */
	const navigateToCategoryById = (): void => {
		const path = route.path.toLowerCase()
		let routeName = "CategoryPage"

		if (path.startsWith("/movies")) {
			routeName = "CategoryMovies"
		} else if (path.startsWith("/series")) {
			routeName = "CategorySeries"
		}

		router.push({
			name: routeName,
			params: { tid: props.category },
		})
	}

	/**
	 * A computed property to determine if arrows should be rendered
	 * based on the preview type and number of items. Please note that
	 * splide arrows are always set to false, this only deals with our
	 * custom slider arrows.
	 */
	const renderArrows = computed(() => {
		if (props.preview === "option1") {
			return categoryData.value.length > 6
		} else if (props.preview === "option2") {
			return categoryData.value.length > 3
		} else {
			return categoryData.value.length > 4
		}
	})

	/**
	 * Computed property for Splide slider options based on the preview type.
	 * Also disables arrows if the number of items is less than or equal to the perPage count.
	 */
	const splideOptions = computed(() => {
		const baseOptions = {
			type: "slide",
			gap: 30,
			arrows: false,

			// props.preview === "option1"
			// 	? itemsList.value.length > 6
			// 	: props.preview === "option2"
			// 	? itemsList.value.length > 3
			// 	: itemsList.value.length > 4,
			pagination: false,
			// padding: "1.5rem",
			breakpoints: {
				3840: {
					perPage: 12,
					gap: 30,
				},
				2560: {
					perPage: props.preview === "option1" ? 8 : props.preview === "option2" ? 5 : 6,
					gap: 25,
				},
				1920: {
					perPage: props.preview === "option1" ? 8 : props.preview === "option2" ? 4 : 5,
					gap: 20,
				},
				1560: {
					perPage: props.preview === "option1" ? 6 : props.preview === "option2" ? 4 : 4,
					gap: 20,
				},
				1360: {
					perPage: props.preview === "option1" ? 7 : props.preview === "option2" ? 4 : 3,
					gap: 20,
				},
				1024: {
					perPage: props.preview === "option1" ? 5 : props.preview === "option2" ? 3 : 3,
					gap: 20,
					drag: "free",
				},
				980: {
					perPage: props.preview === "option1" ? 4 : props.preview === "option2" ? 3 : 2,
					gap: 15,
				},
				768: {
					perPage: props.preview === "option1" ? 4 : props.preview === "option2" ? 4 : 3,
					gap: 15,
				},
				480: {
					perPage: props.preview === "option1" ? 3 : props.preview === "option2" ? 3 : 2,
					gap: 10,
				},
			},
		}

		switch (props.preview) {
			case "option1":
				return { ...baseOptions, perPage: 6 }
			case "option2":
				return { ...baseOptions, perPage: 3 }
			default:
				return { ...baseOptions, perPage: 4 }
		}
	})

	/**
	 * A function to get the correct teaser component based on the preview prop.
	 * @param item
	 * @returns the component to be used for the teaser.
	 */
	const getTeaserComponent = (item: any) => {
		switch (props.preview) {
			case "option1":
				return TeaserSmall
			case "option2":
				return width.value > 768 ? TeaserLarge : TeaserSmall
			default:
				return TeaserWithNumber
		}
	}

	/**
	 * Update visible indices based on Splide state, something we use to
	 * apply specific classes to first and last visible items. This helps
	 * us adjust the layout of preview teasers accordingly.
	 * @param splide
	 */
	const updateVisibleIndices = (splide: SplideType): void => {
		const components = splide.Components
		if (components && components.Controller) {
			const index = components.Controller.getIndex()
			const perPage = splide.options.perPage || 1

			firstVisibleIndex.value = index
			lastVisibleIndex.value = Math.min(index + perPage - 1, categoryData.value.length - 1)
		}
	}

	/**
	 * Watches when splideRef is initialized to set up event listeners
	 * for updating visible indices on slide move and resize. The @function
	 * updateVisibleIndices is called to detect first and last visible items so that
	 * the preview teasers can adjust their layout accordingly.
	 */
	watch(splideRef, (newSplideRef) => {
		if (newSplideRef && newSplideRef.splide) {
			const splide = newSplideRef.splide

			const updateLastIndex = () => {
				const perPage = splide.options.perPage // ← correct way
				lastVisibleIndex.value = perPage - 1
			}

			// Initial calculation
			splide.on("mounted", updateLastIndex)

			// Update on resize (breakpoint change)
			splide.on("resize", updateLastIndex)

			// Update if needed during sliding
			splide.on("move", updateLastIndex)
			// Initial update
			updateVisibleIndices(splide)
			splide.on("moved", () => {
				updateVisibleIndices(splide)
			})
			splide.on("resized", () => {
				updateVisibleIndices(splide)
			})
		}
	})
</script>

<style scoped lang="scss">
	.row-slider-section {
		position: relative;
		// min-height: 372px;

		.splide__arrows {
			opacity: 0;
			z-index: 11;

			@include br.tablet-down {
				display: none;
			}
		}

		&:not(.is-previewing-teaser) {
			&:hover .splide__arrows {
				opacity: 1;
			}
		}
	}
	.splide__slide {
		display: flex;
		justify-content: end;
		position: relative;
		h1 {
			position: absolute;
			left: 0;
			z-index: 0;
			@include br.respond-to(768px) {
				bottom: 0;
			}
		}
		.teaser-with-number {
			position: relative;
			z-index: 1;
		}
		&.top10 {
			@include br.respond-to(768px) {
				padding-left: 55px;
			}
		}
	}
	.search-with-id {
		border: 0px;
		background: transparent;
		color: var(--white);
		display: flex;
		align-items: center;
		gap: 16px;
		@extend .vl-font-size--22;

		@include br.tablet-down {
			font-size: 18px;
			gap: 10px;
		}

		svg {
			width: 16px;
			height: 16px;

			@include br.tablet-down {
				width: 10px;
				height: 10px;
			}
		}
		&:hover {
			cursor: pointer;
		}
	}
	.category-title {
		margin: 30px 0;
		position: relative;
		display: flex;
		align-items: center;
		gap: 40px;
		width: fit-content;

		h2 {
			font-size: 30px;

			@include br.tablet-down {
				font-size: 24px;
			}

			@include br.mobile-down {
				font-size: 20px;
				line-height: 24px;
			}
		}
		@include br.tablet-down {
			gap: 20px;
			margin: 20px 0;
		}
	}

	.input-wrapper {
		overflow: hidden;
		display: inline-block;
		white-space: nowrap;
		width: 220px; // match this to transition target
	}

	.slide-enter-active,
	.slide-leave-active {
		transition: width 0.3s ease, opacity 0.3s ease;
	}

	.slide-enter-from,
	.slide-leave-to {
		width: 0;
		opacity: 0;
	}

	.slide-enter-to,
	.slide-leave-from {
		width: 220px;
		opacity: 1;
	}
</style>

<style lang="scss">
	.hero-section {
		.splide__track {
			padding-top: 0px;
		}
	}
	.splide__arrow {
		background: rgba($color: #000000, $alpha: 0.6) !important;
		width: 55px !important;
		height: 55px !important;
		svg {
			width: 28px;
			flex-shrink: 0;
			height: auto;
		}
		&--prev {
			left: 0px !important;
			background-image: url("../images/arrow.svg") !important;
			background-repeat: no-repeat !important;
			background-position: center !important;
			transform: translate(30px, 50%) !important;
			rotate: 180deg;
			svg {
				display: none;
			}
		}
		&--next {
			right: 0px !important;
			background-image: url("../images/arrow.svg") !important;
			background-repeat: no-repeat !important;
			background-position: center !important;
			transform: translate(30px, -50%) !important;
			svg {
				display: none;
			}
		}
	}

	// .splide__slide {
	// 	min-width: 180px;
	// }

	@include br.respond-to(768px) {
		.numbered-title {
			font-size: 30vw !important;
			left: -2px;
			color: white !important;
			font-weight: bold !important;
			text-shadow: 0 5px 10px rgb(0, 0, 0) !important;
		}
	}
	@include br.respond-to(480px) {
		.numbered-title {
			font-size: 45vw !important;
		}
	}
</style>
